<script setup lang="tsx">
import type { Threat, SaveThreatsRequest, ThreatMeta, RiskThreat, Response } from '@/models/api-generated'
import http from '@/services/http'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ThreatAutocomplete from '../threat/ThreatAutocomplete.vue'
import IconButton from '@/components/shared/IconButton.vue'
import AddNewThreatModal from '../AddNewThreatModal.vue'
import { defaultInstance } from '@/services/utils'

const props = defineProps({
	riskMetaId: { type: Number, required: true },
	isEdit: { type: Boolean, required: true },
})
const model = defineModel<ThreatMeta[]>({ required: true })

const { t } = useI18n()
const selectedThreat = ref<ThreatMeta>()
const showNewThreatModal = ref(false)

function openNewThreatModal() {
	showNewThreatModal.value = true
}

function saveNewThreatFromModal(payload: ThreatMeta) {
	quickCreateThreat(payload)
	showNewThreatModal.value = false
}

watch(selectedThreat, async (val) => {
	if (val) {
		await addSelectedThreat(val)
		selectedThreat.value = undefined
	}
})

async function addSelectedThreat(threat: ThreatMeta) {
	if (!threat || model.value.some((t) => t.id === threat.id)) return
	model.value.push(threat)

	const request: RiskThreat = {
		riskMetaId: props.riskMetaId,
		threatMetaId: threat.id,
	}
	await http.post('api/threat/riskThreatConnections', request)
}

async function quickCreateThreat(payload: ThreatMeta) {
	const threat: Threat = {
		name: payload.threat.name,
		code: payload.threat.code,
		description: payload.threat.description,
		isCustom: true,
	} as Threat

	const threatMeta: ThreatMeta = {
		threat: threat,
		instance: defaultInstance(),
		catalogMeta: payload.catalogMeta,
	} as ThreatMeta

	const request: SaveThreatsRequest = {
		changed: [],
		deleted: [],
		added: [threatMeta],
	}

	const { data } = await http.post<Response<ThreatMeta[]>>(`api/threat/save`, request)
	if (!data.data) return
	const created = data.data[0]
	await addSelectedThreat(created)
}

async function removeThreat(meta: ThreatMeta) {
	const request: RiskThreat = {
		riskMetaId: props.riskMetaId,
		threatMetaId: meta.id,
	}
	await http.delete('api/threat/riskThreatConnections', {
		data: request,
	})

	model.value = model.value.filter((m) => m.threat.id !== meta.threat.id)
}
</script>

<template>
	<div class="card flex-fill">
		<div class="card-body">
			<h6 class="card-title text-center fw-bold">{{ t('risk.threat', 2) }} ({{ model.length }})</h6>

			<ul class="list-unstyled mb-3">
				<li
					v-for="meta in model"
					:key="meta.threat.id"
					:data-test="'threat-item-' + meta.id"
					class="item-accent border rounded px-2 py-1 mb-1 d-flex justify-content-between align-items-center"
				>
					<span v-one-tooltip="meta.threat.description"> {{ meta.threat.name }} ({{ meta.threat.code }}) </span>

					<IconButton
						v-if="props.isEdit && !meta.isDefaultConnection"
						:data-test="'delete-threat-risk' + meta.id"
						class="bi bi-trash text-danger"
						@click="removeThreat(meta)"
						:title="$t('misc.delete')"
						size="fs-5"
					/>
				</li>
			</ul>

			<template v-if="props.isEdit">
				<ThreatAutocomplete
					v-model="selectedThreat"
					:exclude="model.map((m) => m.id)"
					data-test="threat-autocomplete"
					:label="$t('misc.search')"
				/>

				<div class="d-flex justify-content-center mt-3">
					<button
						:data-test="'add-new-threat-' + props.riskMetaId"
						type="button"
						class="btn btn-outline-primary btn-sm"
						@click="openNewThreatModal"
					>
						<i class="bi bi-plus-circle me-1"></i>
						{{ $t('threat.addThreat') }}
					</button>
				</div>

				<AddNewThreatModal v-model="showNewThreatModal" :risk-meta-id="props.riskMetaId" @confirm="saveNewThreatFromModal" />
			</template>
		</div>
	</div>
</template>
